import { toRaw } from 'vue';

import { MEDIA_TYPE_OPTIONS } from '@/app/library/constants';

/**
 * Deeply normalizes filter objects for comparison.
 * Unwraps reactive objects and removes undefined values.
 * Used to compare filter states and detect changes.
 */
export function deepNormalize(filters) {
  if (Array.isArray(filters)) {
    return filters.map(deepNormalize).filter((item) => item !== undefined);
  }
  if (typeof filters === 'object' && filters !== null) {
    const rawObj = toRaw(filters);
    const normalizedObj = Object.entries(rawObj).reduce((acc, [key, value]) => {
      const normalizedValue = deepNormalize(value);
      if (normalizedValue !== undefined) {
        acc[key] = normalizedValue;
      }
      return acc;
    }, {});

    const isObjectEmpty = Object.keys(normalizedObj).length === 0;
    const allKeysAreEmptyArrays = Object.values(normalizedObj).every(
      (value) => Array.isArray(value) && value.length === 0,
    );

    return isObjectEmpty || allKeysAreEmptyArrays ? undefined : normalizedObj;
  }
  return filters;
}

/**
 * Validates that filter timestamps are correctly formatted.
 * Ensures we don't call API with malformed date strings.
 */
export function hasValidTime(filters, pageType) {
  /**
   * To ensure we are only calling the endpoint for valid times
   * This is to exclude calls where the onOrAfter or onOrBefore times are not formatted correctly
   * Ex. 2024-11-01T00 -> we want to exclude calls with this format
   */

  if (filters === undefined) return false;

  let sourceCreated;

  if (pageType === 'topics' || pageType === 'comparison') {
    if (filters?.sourceCreated === undefined) return false;
    sourceCreated = {
      onOrBefore: filters.sourceCreated.onOrBefore,
      onOrAfter: filters.sourceCreated.onOrAfter,
    };
  } else if (pageType === 'trends' && filters.dateRange === 'CUSTOM') {
    if (!filters.customDateRange) return false;
    sourceCreated = {
      onOrAfter: filters.customDateRange.onOrAfter,
      onOrBefore: filters.customDateRange.onOrBefore,
    };
  } else {
    if (!filters.onOrAfter || !filters.onOrBefore) return false;
    sourceCreated = {
      onOrAfter: filters.onOrAfter,
      onOrBefore: filters.onOrBefore,
    };
  }

  // Validate each timestamp has correct format
  return Object.values(sourceCreated).every((v) => {
    if (typeof v !== 'string') return false;

    // Extract time portion safely
    let time;
    if (v.includes('T')) {
      const parts = v.split('T');
      time = parts[1];
    } else if (v.includes(' ')) {
      const parts = v.split(' ');
      time = parts[1];
    } else {
      // No time delimiter found
      return false;
    }

    // Check time format is valid (HH:MM:SS)
    return time && v.includes(':') && time.length === 8;
  });
}

/**
 * Checks if a post is a text or link preview (no visual media).
 *
 * @param {Object} data - Formatted post data
 * @returns {boolean} True if post is text or link type
 */
export function isTextPreview(data) {
  return [MEDIA_TYPE_OPTIONS.TEXT.value, MEDIA_TYPE_OPTIONS.LINK.value].includes(
    data.platformMediaType,
  );
}

/**
 * Finds a post in a list by matching both its source and sourceId.
 *
 * @param {Array} posts - List of posts to search
 * @param {string} source - Post source (e.g. INSTAGRAM, TWITTER)
 * @param {string} sourceId - Post sourceId
 * @returns {Object|undefined} Matching post, if found
 */
export function findPostBySourceAndId(posts, source, sourceId) {
  return posts.find((post) => post.source === source && post.sourceId === sourceId);
}
